import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider, { useAuth } from './providers/AuthProvider';
import InstanceModeProvider from './providers/InstanceModeProvider';
import LabelsProvider from './providers/LabelsProvider';
import I18nProvider from './providers/I18nProvider';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Dashboard from './pages/Dashboard';
import OESManagement from './pages/OESManagement';
import OESAssessment from './pages/OESAssessment';
import OESAssessmentResult from './pages/OESAssessmentResult';
import OESQuestionnaire from './pages/OESQuestionnaire';
import OESQuestionnaireResult from './pages/OESQuestionnaireResult';
import OESRiskMatrix from './pages/OESRiskMatrix';
import Assets from './pages/Assets';
import AssetDetails from './pages/AssetDetails';
import RiskAssessments from './pages/RiskAssessments';
import Vulnerabilities from './pages/Vulnerabilities';
import SecurityIncidents from './pages/SecurityIncidents';
import ThreatActors from './pages/ThreatActors';
import AdminDashboard from './pages/admin/AdminDashboard';
import Users from './pages/admin/Users';
import Assessments from './pages/admin/Assessments';
import AssessmentDetail from './pages/admin/AssessmentDetail';
import CompletedAssessmentsList from './pages/admin/CompletedAssessmentsList';
import OESDetail from './pages/admin/OESDetail';
import Organizations from './pages/admin/Organizations';
import OrganizationDetail from './pages/admin/OrganizationDetail';
import Invitations from './pages/admin/Invitations';
import Settings from './pages/admin/Settings';
import AdminSecurity from './pages/admin/AdminSecurity';
import AdminEventViewer from './pages/admin/AdminEventViewer';
import Reports from './pages/admin/Reports';
import OESProfile from './pages/admin/OESProfile';
import ProfileSettings from './pages/ProfileSettings';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component (for global_admin and assessment_admin)
const AdminRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const role = localStorage.getItem('role');
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Allow global_admin, assessment_admin, and legacy 'admin' role
  const isAdmin = role === 'global_admin' || role === 'assessment_admin' || role === 'admin';
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/oes" element={
        <AdminRoute>
          <OESManagement />
        </AdminRoute>
      } />
      
      <Route path="/oes/:oesId" element={
        <AdminRoute>
          <OESDetail />
        </AdminRoute>
      } />
      
      <Route path="/oes/:oesId/assessment" element={
        <ProtectedRoute>
          <OESAssessment />
        </ProtectedRoute>
      } />
      
      {/* OES Assessment Result: admin only (regular users see thank-you on dashboard) */}
      <Route path="/oes/:oesId/assessment-result" element={
        <AdminRoute>
          <OESAssessmentResult />
        </AdminRoute>
      } />

      <Route path="/oes/:oesId/profile" element={
        <AdminRoute>
          <OESProfile />
        </AdminRoute>
      } />

      <Route path="/oes/:oesId/questionnaire" element={
        <ProtectedRoute>
          <OESQuestionnaire />
        </ProtectedRoute>
      } />
      <Route path="/oes/:oesId/questionnaire-result" element={
        <ProtectedRoute>
          <OESQuestionnaireResult />
        </ProtectedRoute>
      } />
      <Route path="/oes/:oesId/risk-matrix" element={
        <ProtectedRoute>
          <OESRiskMatrix />
        </ProtectedRoute>
      } />
      
      {/* Admin: view any completed assessment by ID */}
      <Route path="/admin/assessment-results/:assessmentId" element={
        <AdminRoute>
          <OESAssessmentResult />
        </AdminRoute>
      } />
      
      <Route path="/assets" element={
        <ProtectedRoute>
          <Assets />
        </ProtectedRoute>
      } />
      
      <Route path="/assets/:id" element={
        <ProtectedRoute>
          <AssetDetails />
        </ProtectedRoute>
      } />
      
      <Route path="/risk-assessments" element={
        <ProtectedRoute>
          <RiskAssessments />
        </ProtectedRoute>
      } />
      
      <Route path="/vulnerabilities" element={
        <ProtectedRoute>
          <Vulnerabilities />
        </ProtectedRoute>
      } />
      
      <Route path="/incidents" element={
        <ProtectedRoute>
          <SecurityIncidents />
        </ProtectedRoute>
      } />
      
      <Route path="/threat-actors" element={
        <ProtectedRoute>
          <ThreatActors />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfileSettings />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      
      <Route path="/admin/users" element={
        <AdminRoute>
          <Users />
        </AdminRoute>
      } />
      
      <Route path="/admin/assessments" element={
        <AdminRoute>
          <Assessments />
        </AdminRoute>
      } />
      
      <Route path="/admin/assessments/:assessmentId" element={
        <AdminRoute>
          <AssessmentDetail />
        </AdminRoute>
      } />
      
      <Route path="/admin/assessment-results" element={
        <AdminRoute>
          <CompletedAssessmentsList />
        </AdminRoute>
      } />
      
      <Route path="/admin/organizations" element={
        <AdminRoute>
          <Organizations />
        </AdminRoute>
      } />
      <Route path="/admin/organizations/:organizationId" element={
        <AdminRoute>
          <OrganizationDetail />
        </AdminRoute>
      } />
      
      <Route path="/admin/settings" element={
        <AdminRoute>
          <Settings />
        </AdminRoute>
      } />
      <Route path="/admin/security" element={
        <AdminRoute>
          <AdminSecurity />
        </AdminRoute>
      } />
      <Route path="/admin/events" element={
        <AdminRoute>
          <AdminEventViewer />
        </AdminRoute>
      } />
      <Route path="/admin/reports" element={
        <AdminRoute>
          <Reports />
        </AdminRoute>
      } />
      <Route path="/admin/invitations" element={
        <AdminRoute>
          <Invitations />
        </AdminRoute>
      } />
      
      {/* Default Route */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <I18nProvider>
        <AuthProvider>
          <LabelsProvider>
            <InstanceModeProvider>
              <div className="App">
                <AppRoutes />
              </div>
            </InstanceModeProvider>
          </LabelsProvider>
        </AuthProvider>
      </I18nProvider>
    </Router>
  );
}

export default App;
